import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";

import { RRule } from "rrule";
import dayjs from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
import isLeapYear from "dayjs/plugin/isLeapYear";

import { Navigate } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import {
  IBlockedDateEvent,
  IEmployeeOdpBlockDate,
  IGroupedBlockedDateEvents,
  IUpdateEmployeesBlockedDate,
  IUpdateOrCreateEmployeesBlockedDates,
  OdpBlockedDateContextType,
  odpDateBoundariesType,
  rruleDateRangeType,
} from "../@types/blockedDates";
import useGetEmployerODPLimitsApiService from "../services/api/GetEmployerODPLimitsApiService";
import { AuthContext } from "../../../providers/AuthProvider";
import useUpdateEmployerODPLimitConfigApiService from "../services/api/UpdateEmployerODPLimitConfigApiService";
import useGetODPLimitBlockedDatesApiService from "../services/api/GetODPLimitBlockedDatesApiService";
import {
  getAllBlockedDateEvents,
  getEmployeeIdsFromBlockedDate,
  getSplitBlockedDates,
} from "../utils/OdpLimitsUtils";
import useUpdateEmployeesBlockedDates from "../services/api/UpdateEmployeesBlockedDatesApiService";
import { useGetBlockReasonsApiService } from "../services/api/GetBlockReasonsFromStrapi";
import { formatDateToUTCWithTimezone } from "../../../utils/DateTime";

dayjs.extend(dayOfYear);
dayjs.extend(isLeapYear);

const frequencyOptions = [
  {
    value: RRule.YEARLY.toString(),
    label: "Year",
    disabled: false,
  },
  {
    value: RRule.MONTHLY.toString(),
    label: "Month",
    disabled: false,
  },
  {
    value: RRule.WEEKLY.toString(),
    label: "Week",
    disabled: false,
  },
];

export const weekdaysOptions = [
  {
    label: "Sun",
    rule: RRule.SU.toString(),
    value: RRule.SU.weekday,
  },
  {
    label: "Mon",
    rule: RRule.MO.toString(),
    value: RRule.MO.weekday,
  },
  {
    label: "Tue",
    rule: RRule.TU.toString(),
    value: RRule.TU.weekday,
  },
  {
    label: "Wed",
    rule: RRule.WE.toString(),
    value: RRule.WE.weekday,
  },
  {
    label: "Thu",
    rule: RRule.TH.toString(),
    value: RRule.TH.weekday,
  },
  {
    label: "Fri",
    rule: RRule.FR.toString(),
    value: RRule.FR.weekday,
  },
  {
    label: "Sat",
    rule: RRule.SA.toString(),
    value: RRule.SA.weekday,
  },
];

let employerOdpLimitsData: any = null;

export const OdpBlockedDatesContext =
  createContext<OdpBlockedDateContextType | null>(null);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const OdpBlockedDatesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    isSuccess: isUpdateSuccess,
    mutate: onUpdate,
    isLoading: isUpdatingLoading,
  } = useUpdateEmployerODPLimitConfigApiService();

  const {
    mutate: updateEmployeesBlockedDatesMutation,
    isLoading: isEmployeeUpdateLoading,
    isSuccess: isEmployeeUpdateSuccess,
  } = useUpdateEmployeesBlockedDates();

  const auth = useContext(AuthContext);

  const { data, refetch } = useGetEmployerODPLimitsApiService();

  const { data: blockReasons } = useGetBlockReasonsApiService();

  const {
    data: allBlockedDates,
    refetch: refetchBlockedDates,
    isLoading: isLoadingBlockedDates,
  } = useGetODPLimitBlockedDatesApiService();

  employerOdpLimitsData = data;

  const [odpBlockedDates, setOdpBlockedDates] = React.useState<
    IBlockedDateEvent[]
  >([]);

  // grouped blocked dates

  const [groupedBlockedDates, setGroupedBlockedDates] =
    React.useState<IGroupedBlockedDateEvents>({
      pastEvents: [],
      futureEvents: [],
      currentEvents: [],
    });

  const [recurrenceStartDate, setRecurrenceStartDate] = useState(new Date());
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(null);

  const [byMonthDayStart, setByMonthDayStart] = useState<number>(0);
  const [byMonthDayEnd, setByMonthDayEnd] = useState<number>(32);

  const [frequencyValue, setFrequencyValue] = useState(RRule.DAILY);

  const [odpDateBoundaries, setOdpDateBoundaries] =
    useState<odpDateBoundariesType>({
      start: null,
      end: null,
    });

  const [isCreateNew, setIsCreateNew] = React.useState(false);

  const [isUpdatedSuccessfully, setIsUpdatedSuccessfully] =
    React.useState(false);
  const [byWeekDays, setByWeekDays] = useState<string>("");

  const [repeatInterval, setRepeatInterval] = React.useState(1);

  const [neverEnds, setNeverEnds] = React.useState(true);

  const [yearCrossingError, setYearCrossingError] = useState<string | null>(
    null
  );

  const [currentBlockedDate, setCurrentBlockedDate] =
    React.useState<IEmployeeOdpBlockDate>({
      id: "",
      title: "",
      rrule: "",
      block_reason: "",
      employees: [],
      created_by: auth?.id ?? null,
      created_at: formatDateToUTCWithTimezone(new Date()),
      created_by_name: null,
      updated_by: null,
      updated_by_name: null,
      updated_at: formatDateToUTCWithTimezone(new Date()),
    });

  const setNewDateRange = (range: rruleDateRangeType): void => {
    const startDate: Date | null = range[0] ? range[0] : null;
    const endDate: Date | null = range[1] ? range[1] : null;

    setOdpDateBoundaries({
      start: startDate,
      end: endDate,
    });

    if (frequencyValue === RRule.YEARLY && startDate && endDate) {
      const startYear = dayjs(startDate).year();
      const endYear = dayjs(endDate).year();

      if (startYear !== endYear) {
        setYearCrossingError(
          `It looks like your date range runs across two different years (${startYear} to ${endYear}). ` +
            `Yearly recurring dates can only be set within the same calendar year.`
        );
      } else {
        setYearCrossingError(null);
      }
    } else {
      setYearCrossingError(null);
    }
  };

  // set the selected block date for updating or deleting
  const updateSelectedBlockDate = (uuid: string): void => {
    if (uuid && auth) {
      const tempDate = odpBlockedDates.filter(
        (blockedDate) => blockedDate.id === uuid
      )[0];

      setCurrentBlockedDate({
        id: tempDate.id,
        title: tempDate.title,
        rrule: tempDate.rrule,
        block_reason: tempDate.block_reason,
        employees: tempDate.employees ? tempDate.employees : [],
        created_by: tempDate.created_by ?? null,
        created_by_name: tempDate.created_by_name ?? null,
        created_at: tempDate.created_at ?? null,
        updated_by: tempDate.updated_by ?? null,
        updated_by_name: tempDate.updated_by_name ?? null,
        updated_at: tempDate.updated_at ?? null,
      });

      const currentRRULE = RRule.fromString(tempDate.rrule);

      setRepeatInterval(currentRRULE.options.interval);

      if (currentRRULE.options.freq === RRule.DAILY) {
        setFrequencyValue(RRule.DAILY);
        setOdpDateBoundaries({
          start: currentRRULE.options.dtstart,
          end: currentRRULE.options.until,
        });
      }

      if (currentRRULE.options.freq === RRule.YEARLY) {
        setFrequencyValue(RRule.YEARLY);

        if (currentRRULE.options.byyearday) {
          const minDayValue = Math.min(...currentRRULE.options.byyearday);
          const maxDayValue = Math.max(...currentRRULE.options.byyearday);

          setOdpDateBoundaries({
            start: dayjs().dayOfYear(minDayValue).toDate(),
            end: dayjs().dayOfYear(maxDayValue).toDate(),
          });
        } else {
          setOdpDateBoundaries({
            start: dayjs(currentRRULE.options.dtstart).toDate(),
            end: dayjs(currentRRULE.options.dtstart).toDate(),
          });
        }

        setRecurrenceEndDate(currentRRULE.options.until);
      }
      if (currentRRULE.options.freq === RRule.MONTHLY) {
        setFrequencyValue(RRule.MONTHLY);
        setByMonthDayStart(Math.min(...currentRRULE.options.bymonthday));
        setByMonthDayEnd(Math.max(...currentRRULE.options.bymonthday));
        setRecurrenceEndDate(currentRRULE.options.until);
      }

      if (currentRRULE.options.freq === RRule.WEEKLY) {
        setFrequencyValue(RRule.WEEKLY);

        let updatedWeeklyDays = " ";
        currentRRULE.options.byweekday.forEach((day: number) => {
          if (day === RRule.SU.weekday) {
            updatedWeeklyDays += `${RRule.SU.toString()} `;
          }
          if (day === RRule.MO.weekday) {
            updatedWeeklyDays += `${RRule.MO.toString()} `;
          }
          if (day === RRule.TU.weekday) {
            updatedWeeklyDays += `${RRule.TU.toString()} `;
          }
          if (day === RRule.WE.weekday) {
            updatedWeeklyDays += `${RRule.WE.toString()} `;
          }
          if (day === RRule.TH.weekday) {
            updatedWeeklyDays += `${RRule.TH.toString()} `;
          }
          if (day === RRule.FR.weekday) {
            updatedWeeklyDays += `${RRule.FR.toString()} `;
          }
          if (day === RRule.SA.weekday) {
            updatedWeeklyDays += `${RRule.SA.toString()} `;
          }
        });

        setByWeekDays(updatedWeeklyDays);

        setRecurrenceEndDate(currentRRULE.options.until);
      }
    }
  };

  // Deleting block dates
  const deleteBlockDate = (id: string): void => {
    const employerBlockedDates = odpBlockedDates.filter(
      (blocked) =>
        !blocked.employees ||
        (blocked.employees && blocked.employees.length === 0)
    );

    const existingEmployerBlockedDates = Array.from(
      new Set(employerBlockedDates.map((item) => item.id))
    )
      .map((mapID) => employerBlockedDates.find((item) => item.id === mapID))
      .map((item: any) => ({
        id: item.id,
        rrule: item.rrule,
        title: item.title,
        block_reason: item.block_reason,
        created_by: item.created_by,
        created_at: item.created_at,
        updated_by: item.updated_by,
        updated_at: item.updated_at,
      }));

    const updatedEmployerBlockedDates = existingEmployerBlockedDates.filter(
      (blockDate) => blockDate.id !== id
    );

    employerOdpLimitsData.instant.blocked_dates.value =
      updatedEmployerBlockedDates;
    employerOdpLimitsData.instant.blocked_dates.enabled = true;
    employerOdpLimitsData.standard.blocked_dates.value =
      updatedEmployerBlockedDates;
    employerOdpLimitsData.standard.blocked_dates.enabled = true;
    onUpdate({
      employerId: auth?.employer.id,
      config: employerOdpLimitsData,
    });
  };

  const setNewTitle = (title: string): void => {
    setCurrentBlockedDate((prevState) => ({
      ...prevState,
      title,
    }));
  };

  const setBlockReason = (blockReason: string): void => {
    setCurrentBlockedDate((prevState) => ({
      ...prevState,
      block_reason: blockReason,
    }));
  };

  const toggleRepeat = (): void => {
    setCurrentBlockedDate((prevState) => ({
      ...prevState,
    }));
  };

  const toggleByWeekDay = (day: string): void => {
    if (byWeekDays.includes(day)) {
      setByWeekDays((prev) => prev.replace(day, "").trim());
    } else {
      setByWeekDays((prev) => `${prev} ${day} `);
    }
  };

  const getNumbersBetween = (min: number, max: number): number[] => {
    const numbers = [];
    for (let i = min; i <= max; i += 1) {
      numbers.push(i);
    }
    return numbers;
  };

  const generateOnceOffRrule = (): string => {
    let ruleFreq = "FREQ=";

    let ruleInterval = "INTERVAL=";

    let ruleDtStart = "";
    let ruleDtEnd = "";

    if (odpDateBoundaries.start) {
      ruleDtStart = `DTSTART:${dayjs(odpDateBoundaries.start).format(
        "YYYYMMDD"
      )}T000000`;
    }

    if (odpDateBoundaries.end) {
      ruleDtEnd = `UNTIL=${dayjs(odpDateBoundaries.end).format(
        "YYYYMMDD"
      )}T000000`;
    }

    if (frequencyValue === RRule.DAILY) {
      // once off date range
      ruleFreq += "DAILY;";
      ruleInterval += `${repeatInterval};`;
    }

    return `${ruleDtStart}\nRRULE:${ruleFreq}${ruleInterval}${ruleDtEnd}`;
  };

  const generateYearlyRrule = (): string => {
    const ruleFreq = "FREQ=YEARLY";

    const ruleInterval = `INTERVAL=${repeatInterval}`;

    const byYearDayStart = dayjs(odpDateBoundaries.start).dayOfYear();

    const byYearDayEnd = dayjs(odpDateBoundaries.end).dayOfYear();

    const byYearDays = getNumbersBetween(byYearDayStart, byYearDayEnd).join();

    const ruleDtStart = `DTSTART:${dayjs(odpDateBoundaries.start).format(
      "YYYYMMDD"
    )}T000000`;

    const ruleDtEnd = `UNTIL=${dayjs(recurrenceEndDate).format(
      "YYYYMMDD"
    )}T000000`;

    const isInLeapYear = dayjs(odpDateBoundaries.start).isLeapYear();
    const month = odpDateBoundaries.start?.getMonth();
    const nthDayOfMonth = odpDateBoundaries.start?.getDate();

    let is29thFeb = false;

    if (isInLeapYear) {
      is29thFeb = month === 1 && nthDayOfMonth === 29;
    }

    if (is29thFeb) {
      return `${ruleDtStart}\nRRULE:${ruleFreq};${ruleInterval};${ruleDtEnd}`;
    }

    return `${ruleDtStart}\nRRULE:${ruleFreq};${ruleInterval};${ruleDtEnd};BYYEARDAY=${byYearDays}`;
  };

  const generateMonthlyRrule = (): string => {
    const ruleDtStart = `DTSTART:${dayjs().format("YYYYMMDD")}T000000`;

    const ruleFreq = "FREQ=MONTHLY";

    const ruleInterval = `INTERVAL=${repeatInterval}`;

    const byMonthDays = getNumbersBetween(
      byMonthDayStart,
      byMonthDayEnd
    ).join();

    const ruleDtEnd = `UNTIL=${dayjs(recurrenceEndDate).format(
      "YYYYMMDD"
    )}T000000`;

    return `${ruleDtStart}\nRRULE:${ruleFreq};${ruleInterval};${ruleDtEnd};BYMONTHDAY=${byMonthDays}`;
  };

  const generateWeeklyRrule = (): string => {
    const ruleDtStart = `DTSTART:${dayjs(new Date()).format(
      "YYYYMMDD"
    )}T000000`;

    const ruleFreq = "FREQ=WEEKLY";

    const ruleInterval = `INTERVAL=${repeatInterval}`;

    const ruleDtEnd = `UNTIL=${dayjs(recurrenceEndDate).format(
      "YYYYMMDD"
    )}T000000`;

    const ruleByWeekDays = byWeekDays.trim().split(/\s+/).join();

    return `${ruleDtStart}\nRRULE:${ruleFreq};${ruleInterval};${ruleDtEnd};BYDAY=${ruleByWeekDays}`;
  };

  const getRecurrenceRule = (): string => {
    let rrule = "";

    if (frequencyValue === RRule.YEARLY) rrule = generateYearlyRrule();

    if (frequencyValue === RRule.MONTHLY) rrule = generateMonthlyRrule();

    if (frequencyValue === RRule.WEEKLY) rrule = generateWeeklyRrule();

    if (frequencyValue === RRule.DAILY) rrule = generateOnceOffRrule();

    return rrule;
  };

  // Create or update individual blocked dates
  const updateOrCreateEmployeesBlockedDates = ({
    employeesCreate,
    employeesDelete,
    blockedDateId,
  }: IUpdateOrCreateEmployeesBlockedDates): void => {
    let newBlockedDateId;

    if (blockedDateId) {
      newBlockedDateId = blockedDateId;
      // Delete company wide blocked date if it exists
      deleteBlockDate(newBlockedDateId);
    } else {
      newBlockedDateId = uuidv4();
    }

    const requestPayload: IUpdateEmployeesBlockedDate = {
      employees_create: getEmployeeIdsFromBlockedDate(employeesCreate),
      employees_remove: getEmployeeIdsFromBlockedDate(employeesDelete),
      blocked_date: {
        id: newBlockedDateId,
        title: currentBlockedDate.title,
        rrule: getRecurrenceRule(),
        block_reason: currentBlockedDate.block_reason,
        created_by: currentBlockedDate.created_by,
        created_at: currentBlockedDate.created_at,
        updated_by: auth?.id || null,
        updated_at: formatDateToUTCWithTimezone(new Date()),
      },
    };

    updateEmployeesBlockedDatesMutation(requestPayload);
  };

  const isValidData = (): boolean => {
    if (currentBlockedDate.title?.trim().length === 0) return false;

    if (currentBlockedDate.block_reason?.trim().length === 0) return false;

    if (repeatInterval < 1 || repeatInterval === undefined) return false;

    if (!(frequencyValue === RRule.DAILY) && !recurrenceEndDate) return false;

    if (frequencyValue === RRule.YEARLY) {
      if (!odpDateBoundaries.start) return false;
      if (!odpDateBoundaries.end) return false;
      if (yearCrossingError) return false;
    }

    if (frequencyValue === RRule.MONTHLY) {
      if (byMonthDayStart < 1 || byMonthDayStart > 31) return false;
      if (byMonthDayEnd < 1 || byMonthDayEnd > 31) return false;

      if (!byMonthDayEnd || byMonthDayStart > byMonthDayEnd) return false;
    }

    if (frequencyValue === RRule.WEEKLY) {
      if (byWeekDays.trim().length === 0) return false;
    }
    if (frequencyValue === RRule.DAILY) {
      if (!odpDateBoundaries.start) return false;
      if (!odpDateBoundaries.end) return false;
    }

    return true;
  };

  // Update or Create blocked dates
  const updateOrCreateNewBlockDates = (): void => {
    const employerBlockedDates = odpBlockedDates.filter(
      (blocked) =>
        !blocked.employees ||
        (blocked.employees && blocked.employees.length === 0)
    );

    const existingEmployerBlockedDates = Array.from(
      new Set(employerBlockedDates.map((item) => item.id))
    )
      .map((id) => employerBlockedDates.find((item) => item.id === id))
      .map((item: any) => ({
        id: item.id,
        rrule: item.rrule,
        title: item.title,
        block_reason: item.block_reason,
        created_by: item.created_by,
        created_at: item.created_at,
        updated_by: item.updated_by,
        updated_at: item.updated_at,
      }));

    let updatedEmployerBlockedDates: {
      id: string;
      title: string;
      rrule: string;
      block_reason: string;
      created_by: number | null;
      created_at: string | null;
      updated_by: number | null;
      updated_at: string | null;
    }[] = [];

    if (existingEmployerBlockedDates.length > 0) {
      updatedEmployerBlockedDates = existingEmployerBlockedDates;
    }

    // add new blockDate to the existing
    const newRruleString = getRecurrenceRule();

    const index = existingEmployerBlockedDates.findIndex(
      (blockDate) => blockDate.id === currentBlockedDate.id
    );

    if (index >= 0) {
      // Update existing block - preserve created_at and created_by
      updatedEmployerBlockedDates[index].rrule = newRruleString;
      updatedEmployerBlockedDates[index].title = currentBlockedDate.title;
      updatedEmployerBlockedDates[index].block_reason =
        currentBlockedDate.block_reason;
      updatedEmployerBlockedDates[index].updated_by = auth?.id || null;
      // IMPORTANT: Use formatDateToUTCWithTimezone, NOT toISOString()
      // Backend expects: 2025-12-19 08:14:42.843000+00:00 (Python datetime format)
      // NOT ISO 8601: 2025-12-19T08:14:42.843Z
      updatedEmployerBlockedDates[index].updated_at =
        formatDateToUTCWithTimezone(new Date());
    } else {
      // Delete individual blocked date if exists
      if (
        currentBlockedDate.employees &&
        currentBlockedDate.employees.length > 0
      ) {
        updateOrCreateEmployeesBlockedDates({
          employeesCreate: [],
          employeesDelete: currentBlockedDate.employees,
          blockedDateId: currentBlockedDate.id,
        });
      }

      updatedEmployerBlockedDates.push({
        id: uuidv4(),
        title: currentBlockedDate.title,
        rrule: newRruleString,
        block_reason: currentBlockedDate.block_reason,
        created_by: auth?.id || null,
        created_at: formatDateToUTCWithTimezone(new Date()),
        updated_by: auth?.id || null,
        updated_at: formatDateToUTCWithTimezone(new Date()),
      });
    }

    employerOdpLimitsData.instant.blocked_dates.value =
      updatedEmployerBlockedDates;
    employerOdpLimitsData.instant.blocked_dates.enabled = true;

    employerOdpLimitsData.standard.blocked_dates.value =
      updatedEmployerBlockedDates;
    employerOdpLimitsData.standard.blocked_dates.enabled = true;

    onUpdate(
      {
        employerId: auth?.employer.id,
        config: employerOdpLimitsData,
      },
      {
        onError: () => {
          showNotification({
            title: "Error Saving Blocked Dates",
            message: "Failed to save blocked dates. Please try again.",
            color: "red",
            autoClose: 4000,
          });
        },
      }
    );
  };

  useEffect(() => {
    if (isUpdateSuccess || isEmployeeUpdateSuccess) {
      setIsUpdatedSuccessfully(true);
      refetch();
      refetchBlockedDates();
    }
  }, [isUpdateSuccess, isEmployeeUpdateSuccess]);

  useEffect(() => {
    if (allBlockedDates) {
      const blockedDates = getAllBlockedDateEvents(allBlockedDates);
      setOdpBlockedDates(blockedDates);
      setGroupedBlockedDates(getSplitBlockedDates(blockedDates));
    }
  }, [allBlockedDates]);

  useEffect(() => {
    if (
      frequencyValue === RRule.MONTHLY &&
      recurrenceEndDate &&
      recurrenceStartDate
    ) {
      setOdpDateBoundaries({
        start: recurrenceStartDate,
        end: recurrenceEndDate,
      });
    }
  }, [recurrenceEndDate, recurrenceStartDate]);

  const value = {
    recurrenceStartDate,
    recurrenceEndDate,
    setRecurrenceStartDate,
    setRecurrenceEndDate,
    frequencyOptions,
    frequencyValue,
    isCreateNew,
    isUpdatedSuccessfully,
    repeatInterval,
    neverEnds,
    odpBlockedDates,
    groupedBlockedDates,
    currentBlockedDate,
    odpDateBoundaries,
    byWeekDays,
    byMonthDayStart,
    byMonthDayEnd,
    setByMonthDayStart,
    setByMonthDayEnd,
    setIsCreateNew,
    setIsUpdatedSuccessfully,
    setRepeatInterval,
    toggleByWeekDay,
    setNeverEnds,
    setNewTitle,
    blockReasons: blockReasons?.data || [],
    setBlockReason,
    setNewDateRange,
    toggleRepeat,
    setInterval,
    updateSelectedBlockDate,
    deleteBlockDate,
    updateOrCreateNewBlockDates,
    updateOrCreateEmployeesBlockedDates,
    isValidData,
    setFrequencyValue,
    setOdpDateBoundaries,
    isUpdatingLoading,
    isEmployeeUpdateLoading,
    isLoadingBlockedDates,
    yearCrossingError, // SW-7558: Expose year-crossing validation error
  };

  const providerMemoizedValue = useMemo(() => value, [value]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <OdpBlockedDatesContext.Provider value={providerMemoizedValue}>
      {children}
    </OdpBlockedDatesContext.Provider>
  );
};
